import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { workspaceWidgetChats } from '../lib/agent_chats';
import { subscribeToWidgetGenerationDismissals, widgetGenerationDismissalsVersion } from '../lib/widgetGenerationNotifications';
import {
  ensureWidgetGenerationTurnReconciler,
  pendingWidgetGenerationTurnsSnapshot,
  subscribeToPendingWidgetGenerationTurns,
  widgetGenerationActivityForChat,
  widgetGenerationBannersForActivities,
} from '../lib/widgetGenerationTurns';

const EMPTY_AGENT_CHATS = Object.freeze([]);

export function usePendingWidgetGenerationTurnsByChatId(workspaceId) {
  const pendingTurns = useSyncExternalStore(
    subscribeToPendingWidgetGenerationTurns,
    pendingWidgetGenerationTurnsSnapshot,
    pendingWidgetGenerationTurnsSnapshot,
  );

  return useMemo(() => {
    const workspaceKey = String(workspaceId);
    return new Map(
      pendingTurns
        .filter((pendingTurn) => pendingTurn.workspaceId === workspaceKey)
        .map((pendingTurn) => [pendingTurn.agentChatId, pendingTurn]),
    );
  }, [pendingTurns, workspaceId]);
}

export function useWidgetGenerationBanners(workspaceId) {
  const agentChats = useLiveQuery(
    async function loadWidgetGenerationBannerChats() {
      return workspaceId ? workspaceWidgetChats(workspaceId) : EMPTY_AGENT_CHATS;
    },
    [workspaceId],
    EMPTY_AGENT_CHATS,
  );
  const pendingTurnsByChatId = usePendingWidgetGenerationTurnsByChatId(workspaceId);
  const dismissalsVersion = useSyncExternalStore(
    subscribeToWidgetGenerationDismissals,
    widgetGenerationDismissalsVersion,
    widgetGenerationDismissalsVersion,
  );
  const banners = useMemo(() => {
    void dismissalsVersion;
    const activities = agentChats.map((agentChat) =>
      widgetGenerationActivityForChat(agentChat, pendingTurnsByChatId.get(String(agentChat.id)) || null),
    );

    return widgetGenerationBannersForActivities(activities);
  }, [agentChats, dismissalsVersion, pendingTurnsByChatId]);

  useEffect(function ensureTurnReconciler() {
    ensureWidgetGenerationTurnReconciler();
  }, []);

  return banners;
}
